import { isArray } from "./array-checks.js";
import { isNumber, isString } from "./primitive-types.js";
import type { BrandedType } from "./branded-type.js";
import type { Nil } from "./primitive-types.js";

export const WhitespaceChar = [ " ", "\t", "\r", "\n" ] as const;
export type WhitespaceChar = typeof WhitespaceChar[ number ];

export type TrimLeft<U extends string, TrimChars extends string = WhitespaceChar> =
  U extends `${ TrimChars }${ infer RemainingText }`
  ? TrimLeft<RemainingText, TrimChars>
  : U;

export type TrimRight<U extends string, TrimChars extends string = WhitespaceChar> =
  U extends `${ infer RemainingText }${ TrimChars }`
  ? TrimRight<RemainingText, TrimChars>
  : U;

export type Trim<U extends string, TrimChars extends string = WhitespaceChar> =
  TrimRight<TrimLeft<U, TrimChars>, TrimChars>;

export type ReplaceAll<Text extends string, TextToReplace extends string, Replacement extends string = ""> =
  Text extends `${ infer TextStart extends string }${ TextToReplace }${ infer TextEnd extends string }`
  ? `${ TextStart }${ Replacement }${ ReplaceAll<TextEnd, TextToReplace, Replacement> }`
  : Text;

export type EmptyStringLiteral = "";
export type EmptyStringBranded = BrandedType<"", "EmptyString">;
export type EmptyString = EmptyStringLiteral | EmptyStringBranded;
export type NonEmptyString = BrandedType<string, "NonEmptyString">;
export type WhitespaceString = BrandedType<string, "WhitespaceString">;

export type NonWhitespaceString = BrandedType<string, "NonWhitespaceString">;

type MakePascalCase<OrigStringRemaining extends string, BuiltString extends string = ""> =
  OrigStringRemaining extends `${ infer FirstPart }${ infer Remaining }`
  ? (FirstPart extends (" " | "-" | "_")
    ? MakePascalCase<Capitalize<Remaining>, BuiltString>
    : MakePascalCase<Remaining, `${ BuiltString }${ FirstPart }`>
  )
  : Capitalize<BuiltString>;

type MakeKebabFromCamel<OrigStringRemaining extends string, BuiltString extends string = ""> =
  OrigStringRemaining extends `${ infer FirstPart }${ infer Remaining }`
  ? MakeKebabFromCamel<Remaining, `${ BuiltString }${ FirstPart extends Lowercase<FirstPart> ? "" : "-" }${ Lowercase<FirstPart> }`>
  : BuiltString;

type MakeCamelFromKebab<OrigStringRemaining extends string, BuiltString extends string = ""> =
  OrigStringRemaining extends `${ infer FirstPart }-${ infer Remaining }`
  ? MakeCamelFromKebab<Capitalize<Remaining>, `${ BuiltString }${ FirstPart }`>
  : `${ BuiltString }${ OrigStringRemaining }`;

export type ToPascalCase<T extends string> = MakePascalCase<T>;
export type CamelToKebabCase<T extends string> = MakeKebabFromCamel<T>;
export type KebabToCamelCase<T extends string> = MakeCamelFromKebab<T>;


export function convertCamelToKebabCase<T extends string> (str: T): CamelToKebabCase<T> {
  return str.replace(/[A-Z]+(?![a-z])|[A-Z]/g, (x, sep) => (sep ? "-" : "") + x.toLowerCase()) as CamelToKebabCase<T>;
}

export function convertKebabToCamelCase<T extends string> (str: T): KebabToCamelCase<T> {
  return str.replace(/-./g, (x) => x[ 1 ].toUpperCase()) as KebabToCamelCase<T>;
}

export function convertToPascalCase<T extends string> (str: T): ToPascalCase<T> {
  return str
    .replace(/[ _-]([^ _-])/g, x => x[ 1 ].toUpperCase()) // Capitalize char after space, -, or _
    .replace(/[^.]/, x => x[ 0 ].toUpperCase()) as ToPascalCase<T>;  // Capitalize first char
}

export function isEmptyString (value: unknown): value is EmptyString {
  return value === "";
}

export function isWhitespaceString (value: unknown): value is WhitespaceString{
  return isString(value) && value.trim() === "";
}

export function isNilOrEmptyString (value: unknown): value is Nil | EmptyString {
  return value == null || isEmptyString(value);
}

export function isNilOrWhiteSpace (value: unknown): value is Nil | WhitespaceString{
  return value == null || isWhitespaceString(value);
}

export function isStringLiteralOfType<const T extends string> (value: unknown, possibleValues: readonly [ ...T[] ]): value is T {
  return isString(value) && isArray(possibleValues) && possibleValues.includes(value as T);
}


export function isNumberLiteralOfType<const T extends number> (value: unknown, possibleValues: readonly [ ...T[] ]): value is T {
  return isNumber(value) && isArray(possibleValues) && possibleValues.includes(value as T);
}

export function defaultIfWhitespace<T, TDefault> (value: T, defaultValue: TDefault): Exclude<T, "" | WhitespaceString> | TDefault {
  return isWhitespaceString(value) ? defaultValue : (value as Exclude<T, "" | WhitespaceString>);
}